import gql from "graphql-tag.macro";
import { submitKeycloakAction } from "Utils/submitKeycloakAction";

const loginUserMutation = gql`
    mutation loginUser($username: String!, $password: String!) {
        loginUser(username: $username, password: $password) {
            access_token
            expires_in
            refresh_expires_in
            refresh_token
            full
        }
    }
`;

const logoutUserMutation = gql`
    mutation logoutUser {
        logoutUser
    }
`;

const refreshTokenMutation = gql`
    mutation refreshUserSession($refreshToken: String!) {
        refreshUserSession(refresh_token: $refreshToken) {
            access_token
            expires_in
            refresh_expires_in
            refresh_token
            full
        }
    }
`;

const triggerPasswordResetQuery = gql`
    mutation triggerPasswordReset($userId: ID!, $redirect: String) {
        triggerPasswordReset(userid: $userId, redirectUrl: $redirect)
    }
`;

const updatePasswordQuery = gql`
    mutation changePassword(
        $currentPassword: String!
        $newPassword: String!
        $confirmation: String!
    ) {
        changePassword(
            currentPassword: $currentPassword
            newPassword: $newPassword
            confirmation: $confirmation
        )
    }
`;

const loadCurrentUserQuery = gql`
    query me {
        me(forceRefresh: true) {
            _id
            firstname
            lastname
            email
            groups {
                ... on ProjectGroup {
                    id
                    name
                    displayName
                    description
                    members {
                        _id
                        firstname
                        lastname
                    }
                    hasRoles
                    lastUpdated
                    hasChildGroups
                }
            }
        }
    }
`;

const loginUrlQuery = gql`
    mutation startLogin($redirect_uri: String!) {
        startLogin(redirect_uri: $redirect_uri)
    }
`;

const redeeemCodeMutation = gql`
    mutation redeemCode($redirect_uri: String!, $params: String!) {
        redeemCode(redirect_uri: $redirect_uri, params: $params) {
            access_token
            expires_in
            refresh_expires_in
            refresh_token
            full
        }
    }
`;

const acceptInviteMutation = gql`
    mutation confirmInvite($projectTokens: [ID], $instanceTokens: [ID]) {
        confirmInvite(projectTokens: $projectTokens, instanceTokens: $instanceTokens) {
            subject
        }
    }
`;

const getOwnInvitesQuery = gql`
    query getOwnInvites {
        me {
            _id
            invites {
                token
                type
            }
        }
    }
`;

const loginGraphQLUser = async ({ formData: { username, password } }, { client }) => {
    const {
        data: { loginUser: data },
    } = await client.mutate({
        mutation: loginUserMutation,
        variables: { username, password },
        fetchPolicy: "no-cache"
    });
    return data;
};

export const loginUser =
    (window?.app?.config?.v3MockApiEnabled
        || process.env.STORYBOOK_IS_STORYBOOK
        || window?.APPVERSION?.directory?.length > 0
        || window?.APPVERSION?.BuildNumber?.length > 0
    )
        ? loginGraphQLUser
        : submitKeycloakAction;

export const logoutUser = ({ client }) =>
    client.mutate({
        mutation: logoutUserMutation,
        fetchPolicy: "no-cache"
    });

export const refreshToken = async (
    { refreshToken: refreshTokenVal },
    { client }
) => {
    const {
        data: { refreshUserSession: data },
    } = await client.mutate({
        mutation: refreshTokenMutation,
        variables: { refreshToken: refreshTokenVal },
        fetchPolicy: "no-cache"
    });
    return data;
};

const triggerPasswordResetGraphQL = async (
    { formData: { username } = {}, userId },
    { client }
) => {
    const { data } = await client.mutate({
        mutation: triggerPasswordResetQuery,
        variables: { userId: userId || username },
        fetchPolicy: "no-cache"
    });
    return data;
};

export const triggerPasswordReset =
    (window?.app?.config?.v3MockApiEnabled || process.env.STORYBOOK_IS_STORYBOOK || !!window?.APPVERSION?.directory)
        ? triggerPasswordResetGraphQL
        : submitKeycloakAction;

const updatePasswordGraphQL = async ({ formData }, { client }) => {
    const pwValues = {
        currentPassword: formData.currentPassword,
        newPassword: formData["password-new"],
        confirmation: formData["password-confirm"],
    };
    const { data } = await client.mutate({
        mutation: updatePasswordQuery,
        variables: { ...pwValues },
        fetchPolicy: "no-cache"
    });
    return data;
};

export const updatePassword =
    (window?.app?.config?.v3MockApiEnabled ||
    process.env.STORYBOOK_IS_STORYBOOK ||
    window?.app?.config?.v3UserManagementEnabled)
        ? updatePasswordGraphQL
        : submitKeycloakAction;

export const loadCurrentUser = async ({ client }) => {
    const {
        data: { me: data },
    } = await client.query({
        query: loadCurrentUserQuery,
        fetchPolicy: "no-cache"
    });
    return data;
};

export const getLoginUrl = async ({ redirect_uri }, { client }) => {
    const {
        data: { startLogin: data },
    } = await client.mutate({
        mutation: loginUrlQuery,
        variables: { redirect_uri },
        fetchPolicy: "no-cache"
    });
    return data;
};

export const redeemCode = async ({ redirect_uri, params }, { client }) => {
    const {
        data: { redeemCode: data },
    } = await client.mutate({
        mutation: redeeemCodeMutation,
        variables: { redirect_uri, params },
        fetchPolicy: "no-cache"
    });
    return data;
};

export const updatePasswordInApp = async ({ confirmation, currentPassword, newPassword }, { client }) => {
    const { data } = await client.mutate({
        mutation: updatePasswordQuery,
        variables: { confirmation, currentPassword, newPassword },
        fetchPolicy: "no-cache"
    });
    return data;
};

export const acceptInvite = async ({ projectTokens, instanceTokens }, { client }) => {
    const { data } = await client.mutate({
        mutation: acceptInviteMutation,
        variables: { projectTokens, instanceTokens },
        fetchPolicy: "no-cache"
    });
    return data;
};

export const getInvites = async ({ client }) => {
    const { data: { me: { invites } } } = await client.query({
        query: getOwnInvitesQuery,
        fetchPolicy: "no-cache"
    });
    return invites;
};

export const triggerEmailVerification =
    (window?.app?.config?.v3MockApiEnabled || process.env.STORYBOOK_IS_STORYBOOK)
        ? // eslint-disable-next-line no-alert
        ({ action }) =>
            console.log(`Submitted email verification to ${action}`)
        : submitKeycloakAction;

export const triggerBackToApplication =
    (window?.app?.config?.v3MockApiEnabled || process.env.STORYBOOK_IS_STORYBOOK)
        ? // eslint-disable-next-line no-alert
        ({ action }) =>
            console.log(`Submitted back to application to ${action}`)
        : submitKeycloakAction;

export const acceptTermsAndConditions =
   (window?.app?.config?.v3MockApiEnabled || process.env.STORYBOOK_IS_STORYBOOK)
       ? // eslint-disable-next-line no-alert
       () => console.log("Accepted terms and conditions")
       : submitKeycloakAction;
